/** The exact check loop of a render (vp/render/qa.py, stored as lint.qa) and the knobs it was rendered with. */
type Frame = { frame: number; t: number; expect: boolean; cells: number; bbox: number[] | null };
type Finding = { error_type: string; why: string };
export type QaReport = { ok: boolean; findings: Finding[]; frames: Frame[]; checked: number };

export function qaReport(lint: Record<string, unknown>): QaReport | null {
  const q = lint.qa;
  if (!q || typeof q !== "object" || Array.isArray(q)) return null;
  const o = q as Partial<QaReport>;
  return { ok: o.ok === true, findings: o.findings ?? [], frames: o.frames ?? [], checked: o.checked ?? 0 };
}

export function ParamsLine({ params }: { params: Record<string, unknown> }) {
  const keys = Object.keys(params);
  if (!keys.length) return null;
  const preset = typeof params.preset === "string" ? params.preset : null;
  const rest = keys.filter((k) => k !== "preset").map((k) => `${k}=${JSON.stringify(params[k])}`);
  return (
    <p className="ws8-muted" data-testid="render-params">
      {preset ? <>Version <strong>{preset}</strong></> : "Params"}{rest.length ? ` · ${rest.join(", ")}` : ""}
    </p>
  );
}

export function CheckLoop({ lint }: { lint: Record<string, unknown> }) {
  const q = qaReport(lint);
  if (!q) return <p className="ws8-muted" data-testid="qa-check-none">No exact check recorded for this render.</p>;
  return (
    <div data-testid="qa-check">
      <p>
        <span className={`ws8-badge ${q.ok ? "ws8-badge-ok" : "ws8-badge-bad"}`}>{q.ok ? "pass" : "fail"}</span>{" "}
        {q.checked} frame{q.checked === 1 ? "" : "s"} diffed against the plate alone
      </p>
      {q.findings.length > 0 && (
        <ul data-testid="qa-findings">{q.findings.map((f, i) => <li key={i}><strong>{f.error_type}</strong>: {f.why}</li>)}</ul>
      )}
      {q.frames.length > 0 && (
        <table className="ws8-table">
          <caption>Sampled frames</caption>
          <thead><tr><th scope="col">Time (s)</th><th scope="col">Frame</th><th scope="col">Line due</th>
            <th scope="col">Type cells</th><th scope="col">Type box (x0, y0, x1, y1)</th></tr></thead>
          <tbody>
            {q.frames.map((f) => (
              <tr key={f.frame}>
                <td>{f.t.toFixed(2)}</td><td>{f.frame}</td><td>{f.expect ? "yes" : "no"}</td><td>{f.cells}</td>
                <td>{f.bbox ? f.bbox.join(", ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
